import { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import type { DailySnapshotRow } from '@/lib/supabase-to-sem';
import type { RestaurantSemData } from '@/types/sem';
import { normalizeRestaurantName } from '@/lib/normalize-names';

interface TrendsTabProps {
  dailySnapshots: DailySnapshotRow[];
  restaurants?: RestaurantSemData[];
}

type MetricKey = 'spend' | 'conversions' | 'roas' | 'cpa' | 'clicks';

const METRICS: { key: MetricKey; label: string; color: string; formatter: (v: number) => string }[] = [
  { key: 'spend', label: 'Spend', color: 'rgba(255,255,255,0.5)', formatter: (v) => `$${v.toFixed(0)}` },
  { key: 'conversions', label: 'Conversions', color: 'rgba(255,255,255,0.3)', formatter: (v) => v.toFixed(0) },
  { key: 'roas', label: 'ROAS', color: '#4aba7a', formatter: (v) => `${v.toFixed(2)}x` },
  { key: 'cpa', label: 'CPA', color: '#e0a852', formatter: (v) => `$${v.toFixed(0)}` },
  { key: 'clicks', label: 'Clicks', color: '#e05a52', formatter: (v) => v.toFixed(0) },
];

function formatCurrency(v: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

export function TrendsTab({ dailySnapshots, restaurants }: TrendsTabProps) {
  const [selectedMetrics, setSelectedMetrics] = useState<MetricKey[]>(['spend', 'conversions']);
  const [selectedRestaurant, setSelectedRestaurant] = useState<string>('all');

  // Filter snapshots by restaurant
  const filteredSnapshots = useMemo(() => {
    if (selectedRestaurant === 'all') return dailySnapshots;
    return dailySnapshots.filter(s => s.restaurant_id === selectedRestaurant);
  }, [dailySnapshots, selectedRestaurant]);

  // Aggregate by date
  const chartData = useMemo(() => {
    const byDate = new Map<string, { spend: number; impressions: number; clicks: number; conversions: number; conversion_value: number }>();

    for (const s of filteredSnapshots) {
      const existing = byDate.get(s.date) || { spend: 0, impressions: 0, clicks: 0, conversions: 0, conversion_value: 0 };
      existing.spend += s.spend;
      existing.impressions += s.impressions;
      existing.clicks += s.clicks;
      existing.conversions += s.conversions;
      existing.conversion_value += s.conversion_value;
      byDate.set(s.date, existing);
    }

    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, totals]) => ({
        date: new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        rawDate: date,
        spend: totals.spend,
        conversions: totals.conversions,
        clicks: totals.clicks,
        impressions: totals.impressions,
        roas: totals.spend > 0 ? totals.conversion_value / totals.spend : 0,
        cpa: totals.conversions > 0 ? totals.spend / totals.conversions : 0,
      }));
  }, [filteredSnapshots]);

  // Summary stats for period
  const periodStats = useMemo(() => {
    const totalSpend = filteredSnapshots.reduce((s, r) => s + r.spend, 0);
    const totalConvs = filteredSnapshots.reduce((s, r) => s + r.conversions, 0);
    const totalValue = filteredSnapshots.reduce((s, r) => s + r.conversion_value, 0);
    const totalClicks = filteredSnapshots.reduce((s, r) => s + r.clicks, 0);
    const roas = totalSpend > 0 ? totalValue / totalSpend : 0;
    const cpa = totalConvs > 0 ? totalSpend / totalConvs : 0;
    const cpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
    const days = chartData.length;
    const avgDailySpend = days > 0 ? totalSpend / days : 0;
    return { totalSpend, totalConvs, roas, cpa, cpc, avgDailySpend, days };
  }, [filteredSnapshots, chartData]);

  // Detect data gaps (>1 day between points)
  const dataGaps = useMemo(() => {
    const gaps: string[] = [];
    for (let i = 1; i < chartData.length; i++) {
      const prev = new Date(chartData[i - 1].rawDate);
      const curr = new Date(chartData[i].rawDate);
      const diffDays = (curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24);
      if (diffDays > 2) {
        gaps.push(chartData[i].date);
      }
    }
    return gaps;
  }, [chartData]);

  // Period avg for ROAS
  const avgRoas = useMemo(() => {
    if (chartData.length === 0) return 0;
    return chartData.reduce((s, d) => s + d.roas, 0) / chartData.length;
  }, [chartData]);

  const toggleMetric = (key: MetricKey) => {
    setSelectedMetrics((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  return (
    <div className="space-y-6">
      {/* Top bar: filter + metric toggles */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {METRICS.map((m) => (
            <button
              key={m.key}
              onClick={() => toggleMetric(m.key)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                selectedMetrics.includes(m.key)
                  ? 'border-[var(--ed-rule-heavy)] bg-[var(--ed-bg-3)] text-[var(--ed-t1)]'
                  : 'border-[var(--ed-rule)] bg-[var(--ed-bg-2)] text-[var(--ed-t3)] hover:text-[var(--ed-t1)]'
              }`}
            >
              <span
                className="inline-block w-2 h-2 rounded-full mr-1.5"
                style={{ backgroundColor: m.color }}
              />
              {m.label}
            </button>
          ))}
        </div>

        {/* Restaurant filter */}
        {restaurants && restaurants.length > 0 && (
          <div className="relative">
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="appearance-none bg-[var(--ed-bg-2)] border border-[var(--ed-rule)] rounded-md px-3 py-1.5 pr-8 text-xs text-[var(--ed-t1)] cursor-pointer hover:border-[var(--ed-rule-heavy)] transition-colors"
            >
              <option value="all">All Restaurants</option>
              {restaurants.map(r => (
                <option key={r.id} value={r.id}>
                  {normalizeRestaurantName(r.name)}
                </option>
              ))}
            </select>
            <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[var(--ed-t3)] pointer-events-none" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5l3 3 3-3" /></svg>
          </div>
        )}
      </div>

      {/* Period summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        {[
          { label: 'Period Spend', value: formatCurrency(periodStats.totalSpend) },
          { label: 'Conversions', value: Math.round(periodStats.totalConvs).toLocaleString() },
          { label: 'Avg ROAS', value: `${periodStats.roas.toFixed(2)}x`, color: 'text-[var(--ed-green)]' },
          { label: 'Avg CPA', value: `$${periodStats.cpa.toFixed(2)}` },
          { label: 'Avg CPC', value: `$${periodStats.cpc.toFixed(2)}` },
          { label: 'Avg Daily Spend', value: formatCurrency(periodStats.avgDailySpend) },
        ].map(s => (
          <div key={s.label} className="ed-card px-4 py-3">
            <div className="text-[0.55rem] text-muted-foreground/40 uppercase tracking-wider">{s.label}</div>
            <div className={`text-lg font-display font-light tabular-nums mt-0.5 ${s.color || ''}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Main chart */}
      <div className="ed-card p-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-[0.65rem] text-muted-foreground/50 uppercase tracking-wider">
            {selectedRestaurant === 'all' ? 'Portfolio' : normalizeRestaurantName(restaurants?.find(r => r.id === selectedRestaurant)?.name || '')} Performance
          </span>
          {dataGaps.length > 0 && (
            <span className="text-[0.6rem] text-[var(--ed-amber)] bg-[var(--ed-amber-bg)] px-2 py-0.5 rounded border border-[rgba(224,168,82,0.12)]">
              {dataGaps.length} data gap{dataGaps.length !== 1 ? 's' : ''} detected
            </span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'rgba(255,255,255,0.38)', fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'rgba(255,255,255,0.06)' }}
            />
            {selectedMetrics.map((key, i) => {
              const metric = METRICS.find((m) => m.key === key)!;
              return (
                <YAxis
                  key={key}
                  yAxisId={key}
                  orientation={i === 0 ? 'left' : 'right'}
                  tick={{ fill: metric.color, fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={metric.formatter}
                  hide={i > 1}
                />
              );
            })}
            <Tooltip
              contentStyle={{
                backgroundColor: '#141418',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px',
                fontSize: '12px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.3)',
              }}
            />
            <Legend />
            {/* Show avg ROAS line if ROAS is selected */}
            {selectedMetrics.includes('roas') && avgRoas > 0 && (
              <ReferenceLine
                yAxisId="roas"
                y={avgRoas}
                stroke="#4aba7a"
                strokeDasharray="6 4"
                strokeOpacity={0.4}
                label={{ value: `Avg ${avgRoas.toFixed(1)}x`, fill: '#4aba7a', fontSize: 10, position: 'insideTopRight' }}
              />
            )}
            {/* Data gap markers */}
            {dataGaps.map(gap => (
              <ReferenceLine
                key={gap}
                x={gap}
                yAxisId={selectedMetrics[0]}
                stroke="rgba(224,168,82,0.3)"
                strokeDasharray="4 4"
              />
            ))}
            {selectedMetrics.map((key) => {
              const metric = METRICS.find((m) => m.key === key)!;
              return (
                <Line
                  key={key}
                  yAxisId={key}
                  type="monotone"
                  dataKey={key}
                  stroke={metric.color}
                  strokeWidth={2}
                  dot={false}
                  name={metric.label}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Secondary chart: ROAS + CPA */}
      <div className="grid grid-cols-2 gap-4">
        <div className="ed-card p-5">
          <div className="text-[0.6rem] text-muted-foreground/40 uppercase tracking-wider mb-3">ROAS Trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
              <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 9 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#4aba7a', fontSize: 9 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v.toFixed(0)}x`} />
              <Tooltip contentStyle={{ backgroundColor: '#141418', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', fontSize: '11px' }} formatter={(v: number) => [`${v.toFixed(2)}x`, 'ROAS']} />
              <ReferenceLine y={avgRoas} stroke="#4aba7a" strokeDasharray="6 4" strokeOpacity={0.3} />
              <Line type="monotone" dataKey="roas" stroke="#4aba7a" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="ed-card p-5">
          <div className="text-[0.6rem] text-muted-foreground/40 uppercase tracking-wider mb-3">CPA Trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
              <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.25)', fontSize: 9 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#e0a852', fontSize: 9 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v.toFixed(0)}`} />
              <Tooltip contentStyle={{ backgroundColor: '#141418', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', fontSize: '11px' }} formatter={(v: number) => [`$${v.toFixed(2)}`, 'CPA']} />
              <Line type="monotone" dataKey="cpa" stroke="#e0a852" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
